/**
 * ResumeDraftBanner — shown above the RequestForm when the user has ≥1 open draft
 * and has NOT navigated to a specific session URL.
 *
 * Non-blocking: renders as a MUI Alert (role="status") so it doesn't block the
 * RequestForm beneath it. The user can dismiss (Start new) or resume.
 *
 * ui-ux-pro-max: 8dp spacing, palette colors only, 44px touch targets, role="status".
 */

import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/AuthContext';
import { clearActiveSessionId } from '@/hooks/useIntakeDraftStorage';
import { useIntakeDrafts } from '@/hooks/useIntakeDrafts';
import type { IntakeDraftSummary } from '@/types/intake';

const SNIPPET_MAX = 60;

function snippet(text: string): string {
  if (text.length <= SNIPPET_MAX) return text;
  return `${text.slice(0, SNIPPET_MAX).trimEnd()}…`;
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

interface ResumeDraftBannerProps {
  /** Called when the user dismisses ("Start new") — clears activeSessionId storage. */
  onDismiss?: () => void;
}

export function ResumeDraftBanner({ onDismiss }: ResumeDraftBannerProps): ReactElement | null {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { drafts } = useIntakeDrafts();
  const [dismissed, setDismissed] = useState(false);

  // Reset dismissed if drafts change (e.g. new session started).
  useEffect(() => {
    setDismissed(false);
  }, [drafts?.length]);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    if (user?.id) clearActiveSessionId(user.id);
    onDismiss?.();
  }, [user?.id, onDismiss]);

  const handleResume = useCallback(
    (draft: IntakeDraftSummary) => {
      navigate(`/requests/new/${draft.id}`);
    },
    [navigate],
  );

  const handleViewDrafts = useCallback(() => {
    navigate('/requests?tab=drafts');
  }, [navigate]);

  // Not ready yet, dismissed, or no open drafts.
  if (drafts === null || dismissed || drafts.length === 0) return null;

  const latest: IntakeDraftSummary | undefined = drafts[0]; // newest first from the server
  if (!latest) return null;

  if (drafts.length === 1) {
    return (
      <Alert
        severity="info"
        role="status"
        sx={{ mb: 2 }}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              color="inherit"
              variant="outlined"
              onClick={() => handleResume(latest)}
              sx={{ minHeight: 44, whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              Resume
            </Button>
            <Button
              size="small"
              color="inherit"
              onClick={handleDismiss}
              sx={{ minHeight: 44, whiteSpace: 'nowrap', textTransform: 'none' }}
            >
              Start new
            </Button>
          </Stack>
        }
      >
        <Box>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Resume your draft
          </Typography>
          <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
            from {relativeTime(latest.updated_at)}
          </Typography>
          {latest.attachment_count > 0 && (
            <Typography variant="body2" component="span" color="text.secondary" sx={{ ml: 0.5 }}>
              · {latest.attachment_count} {latest.attachment_count === 1 ? 'attachment' : 'attachments'}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            &ldquo;{snippet(latest.initial_request)}&rdquo;
          </Typography>
        </Box>
      </Alert>
    );
  }

  // N > 1 drafts.
  return (
    <Alert
      severity="info"
      role="status"
      sx={{ mb: 2 }}
      action={
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            onClick={handleViewDrafts}
            sx={{ minHeight: 44, whiteSpace: 'nowrap', textTransform: 'none' }}
          >
            View drafts
          </Button>
          <Button
            size="small"
            color="inherit"
            variant="outlined"
            onClick={() => handleResume(latest)}
            sx={{ minHeight: 44, whiteSpace: 'nowrap', textTransform: 'none' }}
          >
            Resume latest
          </Button>
          <Button
            size="small"
            color="inherit"
            onClick={handleDismiss}
            sx={{ minHeight: 44, whiteSpace: 'nowrap', textTransform: 'none' }}
          >
            Start new
          </Button>
        </Stack>
      }
    >
      <Typography variant="body2">
        You have{' '}
        <strong>{drafts.length}</strong> drafts in progress.
      </Typography>
    </Alert>
  );
}
